import React from 'react';
import './AislePage.css';
import { getSubcategoryDisplay } from '../utils/taxonomy';

// ── AislePage ─────────────────────────────────────────────────────────────────
// Second level of progressive disclosure: shows subcategories within an aisle.
// Falls back to a flat product list if products don't have a `subcategory` field yet.
//
// Props:
//   aisle                — aisle object { id, name, icon, categories }
//   products             — displayProducts already filtered to this aisle's categories
//   onSelectSubcategory  — called with subcategory { id, name, icon, count }
//   onSelectProduct      — called when a product is tapped (fallback flat-list mode)
//   onAddToBasket        — called with (product, prices) from quick-add button
//   onBack               — back to aisle list

function AislePage({ aisle, products, onSelectSubcategory, onSelectProduct, onAddToBasket, onBack }) {

  // Group products by subcategory
  const subcategoryMap = {};
  for (const p of products) {
    const key = p.subcategory || null;
    if (key) {
      if (!subcategoryMap[key]) subcategoryMap[key] = [];
      subcategoryMap[key].push(p);
    }
  }

  const subcategoryIds  = Object.keys(subcategoryMap);
  const hasSubcategories = subcategoryIds.length > 0;

  // Sort subcategories alphabetically by display name
  const sortedSubcategories = subcategoryIds
    .map(id => ({
      id,
      ...getSubcategoryDisplay(id),
      count: subcategoryMap[id].length,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Fallback: products without a subcategory field (shown as flat list)
  const unsortedProducts = hasSubcategories
    ? products.filter(p => !p.subcategory)
    : [...products];
  const flatProducts = [...unsortedProducts].sort((a, b) => a.name.localeCompare(b.name));

  const getCheapestPrice = (product) => {
    const valid = product.prices?.filter(p => p.price > 0);
    if (!valid?.length) return null;
    return Math.min(...valid.map(p => p.price));
  };

  return (
    <div className="aisle-page">

      {/* Back button */}
      <button className="ap-back-btn" onClick={onBack}>
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="13 18 7 12 13 6"/>
        </svg>
        Browse
      </button>

      {/* Aisle header */}
      <div className="ap-header">
        <span className="ap-header-icon" aria-hidden="true">{aisle.icon}</span>
        <h1 className="ap-title">{aisle.name}</h1>
      </div>

      {products.length === 0 ? (
        <div className="ap-empty">No products in this aisle yet.</div>
      ) : hasSubcategories ? (
        <>
          {/* Subcategory list */}
          <div className="ap-list">
            {sortedSubcategories.map(sub => (
              <div
                key={sub.id}
                className="ap-row"
                role="button"
                tabIndex={0}
                onClick={() => onSelectSubcategory(sub)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectSubcategory(sub);
                  }
                }}
                aria-label={`Browse ${sub.name}`}
              >
                <span className="ap-row-icon" aria-hidden="true">{sub.icon}</span>
                <span className="ap-row-name">{sub.name}</span>
                <span className="ap-row-count">{sub.count} {sub.count === 1 ? 'item' : 'items'}</span>
                <span className="ap-row-chevron" aria-hidden="true">›</span>
              </div>
            ))}
          </div>

          {/* Any products without a subcategory — show as "Other" at the bottom */}
          {flatProducts.length > 0 && (
            <div className="ap-list ap-list-other">
              <div className="ap-other-label">Other</div>
              {flatProducts.map(product => (
                <AisleProductRow
                  key={product.id}
                  product={product}
                  cheapest={getCheapestPrice(product)}
                  onSelectProduct={onSelectProduct}
                  onAddToBasket={onAddToBasket}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        /* Fallback: no subcategory field yet — show products directly */
        <>
          <p className="ap-hint">
            Tap a product to compare prices.
          </p>
          <div className="ap-list">
            {flatProducts.map(product => (
              <AisleProductRow
                key={product.id}
                product={product}
                cheapest={getCheapestPrice(product)}
                onSelectProduct={onSelectProduct}
                onAddToBasket={onAddToBasket}
              />
            ))}
          </div>
        </>
      )}

    </div>
  );
}

// ── Shared product row (used in fallback flat list) ───────────────────────────
function AisleProductRow({ product, cheapest, onSelectProduct, onAddToBasket }) {
  const tierClass = product.ownBrand && product.tier ? product.tier : null;

  const handleAdd = (e) => {
    e.stopPropagation();
    if (onAddToBasket) onAddToBasket(product, product.prices || []);
  };

  return (
    <div className="ap-product-row" onClick={() => onSelectProduct(product)} role="button" tabIndex={0}>
      <div className="ap-product-info">
        <div className="ap-product-name">{product.name}</div>
        <div className="ap-product-meta">
          {product.size && <span>{product.size}</span>}
          {tierClass && (
            <span className={`val-badge ${tierClass}`}>
              {tierClass === 'basic' ? 'Basic' : tierClass.charAt(0).toUpperCase() + tierClass.slice(1)}
            </span>
          )}
        </div>
      </div>
      {cheapest !== null && (
        <span className="ap-from">From £{cheapest.toFixed(2)}</span>
      )}
      <button className="ap-add-btn" onClick={handleAdd} aria-label={`Add ${product.name} to list`}>+</button>
    </div>
  );
}

export default AislePage;
